import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Plus, FileText, ChevronRight, Zap } from "lucide-react";
import axios from "axios";

export default function DashboardPage() {
  const { token, user, API } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ total_campaigns: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/campaigns`, { headers }),
      axios.get(`${API}/dashboard/stats`, { headers }),
    ])
      .then(([campRes, statsRes]) => {
        setCampaigns(campRes.data.campaigns || []);
        setStats(statsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, API]);

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toISOString().split("T")[0];
  };

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 animate-in" style={{ animationDelay: "0.05s" }}>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] mb-2">
            Dashboard
          </p>
          <h1
            className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Welcome back, {user?.name?.split(" ")[0] || "User"}
          </h1>
        </div>
        <Button
          onClick={() => navigate("/create")}
          data-testid="create-campaign-btn"
          className="bg-primary hover:bg-primary/85 text-primary-foreground rounded-sm h-10 px-5 text-sm font-medium active:scale-95 transition-transform"
        >
          <Plus size={16} className="mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 animate-in" style={{ animationDelay: "0.1s" }}>
        <div className="bg-card border border-border p-6 rounded-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] mb-1">
            Total Campaigns
          </p>
          <p className="text-3xl font-extrabold text-foreground" style={{ fontFamily: "Chivo, sans-serif" }}>
            {stats.total_campaigns}
          </p>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] mb-1">
            AI Model
          </p>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            aalign AI Engine
          </p>
        </div>
        <div className="bg-card border border-border p-6 rounded-sm">
          <p className="text-xs text-muted-foreground uppercase tracking-[0.2em] mb-1">
            Status
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-sm text-green-500">System Online</span>
          </div>
        </div>
      </div>

      {/* Campaign List */}
      <div className="animate-in" style={{ animationDelay: "0.15s" }}>
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-base font-bold text-foreground tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Recent Campaigns
          </h2>
        </div>

        {loading ? (
          <div className="bg-card border border-border p-12 flex items-center justify-center rounded-sm">
            <div className="flex gap-2">
              <div className="ai-dot dot-bounce-1" />
              <div className="ai-dot dot-bounce-2" />
              <div className="ai-dot dot-bounce-3" />
            </div>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-card border border-border p-12 flex flex-col items-center justify-center rounded-sm">
            <Zap size={32} className="text-accent mb-4" />
            <p className="text-sm text-muted-foreground mb-1">No campaigns yet</p>
            <p className="text-xs text-muted-foreground mb-6">
              Create your first AI-powered campaign from a brief.
            </p>
            <Button
              onClick={() => navigate("/create")}
              data-testid="empty-create-btn"
              className="bg-primary hover:bg-primary/85 text-primary-foreground rounded-sm h-9 px-4 text-sm"
            >
              <Plus size={14} className="mr-1" />
              Create Campaign
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns.map((c, i) => (
              <button
                key={c.id}
                onClick={() => navigate(`/campaign/${c.id}`)}
                className="campaign-card w-full text-left bg-card border border-border p-5 rounded-sm flex items-center justify-between group"
                data-testid={`campaign-card-${i}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-9 h-9 bg-secondary border border-border flex items-center justify-center rounded-sm flex-shrink-0">
                    <FileText size={16} className="text-accent" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {c.campaign_data?.campaign_name || "Untitled Campaign"}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-muted-foreground">
                        {c.campaign_data?.type || "—"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(c.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRight
                  size={16}
                  className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
